import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Check, ChevronLeft, ChevronRight, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { platformApi, type ExchangeConnectionRegistrationOptions, type MailboxAllocation, type MailboxAllocationConnection } from '../../services/platformApi';
import './EmailAllocationsManagement.css';

const PAGE_SIZE = 50;
const CONNECTION_PAGE_SIZE = 100;

async function loadConnections(): Promise<MailboxAllocationConnection[]> {
  const connections: MailboxAllocationConnection[] = [];
  let skip = 0;
  let page: MailboxAllocationConnection[];
  do {
    const result = await platformApi.mailboxAllocationConnections({ skip, take: CONNECTION_PAGE_SIZE });
    page = result.items;
    connections.push(...page);
    skip += page.length;
  } while (page.length === CONNECTION_PAGE_SIZE);
  return connections;
}

export function EmailAllocationsManagement() {
  const [allocations, setAllocations] = useState<MailboxAllocation[]>([]);
  const [connections, setConnections] = useState<MailboxAllocationConnection[]>([]);
  const [registrationOptions, setRegistrationOptions] = useState<ExchangeConnectionRegistrationOptions | null>(null);
  const [registrationTenantId, setRegistrationTenantId] = useState('');
  const [registrationLoading, setRegistrationLoading] = useState(true);
  const [registrationReload, setRegistrationReload] = useState(0);
  const [registeringConnection, setRegisteringConnection] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [skip, setSkip] = useState(0);
  const [reload, setReload] = useState(0);
  const [connectionId, setConnectionId] = useState('');
  const [mailboxObjectId, setMailboxObjectId] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allocationPage, enabledConnections] = await Promise.all([
        platformApi.mailboxAllocations({ skip, take: PAGE_SIZE + 1 }),
        loadConnections(),
      ]);
      setAllocations(allocationPage.items.slice(0, PAGE_SIZE));
      setHasNext(allocationPage.items.length > PAGE_SIZE);
      setConnections(enabledConnections);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to load mailbox allocations.');
    } finally {
      setLoading(false);
    }
  }, [skip, reload]);

  useEffect(() => { void load(); }, [load]);

  const loadRegistrationOptions = useCallback(async () => {
    setRegistrationLoading(true);
    setRegistrationError(null);
    try {
      const result = await platformApi.exchangeConnectionRegistrationOptions({ skip: 0, take: 200 });
      setRegistrationOptions(result);
      setRegistrationTenantId(current => result.tenants.some(tenant => tenant.tenantId === current)
        ? current : result.tenants[0]?.tenantId ?? '');
    } catch (cause) {
      setRegistrationError(cause instanceof Error ? cause.message : 'Unable to load Exchange Key Vault connection options.');
    } finally {
      setRegistrationLoading(false);
    }
  }, [registrationReload]);

  useEffect(() => { void loadRegistrationOptions(); }, [loadRegistrationOptions]);

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const connection = connections.find(item => `${item.tenantId}:${item.connectionId}` === connectionId);
    if (!connection || !mailboxObjectId.trim()) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await platformApi.createMailboxAllocation({
        tenantId: connection.tenantId,
        connectionId: connection.connectionId,
        mailboxObjectId: mailboxObjectId.trim(),
      });
      setMailboxObjectId('');
      setSkip(0);
      setNotice('Mailbox ownership allocation created.');
      setReload(value => value + 1);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to create mailbox allocation.');
    } finally {
      setSaving(false);
    }
  };

  const handleRegisterConnection = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!registrationTenantId || !registrationOptions) return;
    setRegisteringConnection(true);
    setRegistrationError(null);
    setError(null);
    setNotice(null);
    try {
      const created = await platformApi.createExchangeConnection(registrationTenantId);
      setConnectionId(`${created.tenantId}:${created.connectionId}`);
      setSkip(0);
      setNotice(`Exchange connection registered for ${created.tenantName}. You can now allocate the mailbox object ID.`);
      setReload(value => value + 1);
      setRegistrationReload(value => value + 1);
    } catch (cause) {
      setRegistrationError(cause instanceof Error ? cause.message : 'Unable to register the Exchange connection.');
    } finally {
      setRegisteringConnection(false);
    }
  };

  const handleRemove = async (allocation: MailboxAllocation) => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await platformApi.removeMailboxAllocation(allocation);
      setConfirming(null);
      setNotice('Mailbox ownership allocation revoked.');
      setReload(value => value + 1);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to revoke mailbox allocation.');
    } finally {
      setSaving(false);
    }
  };

  const allocationKey = (allocation: MailboxAllocation) =>
    `${allocation.tenantId}:${allocation.connectionId}:${allocation.mailboxObjectId}`;

  return (
    <section className="email-allocations" aria-labelledby="email-allocations-title">
      <div className="email-allocations__heading">
        <div>
          <p className="email-allocations__eyebrow">Platform controls</p>
          <h2 id="email-allocations-title">Mailbox ownership allocations</h2>
        </div>
        <button className="email-allocations__icon-button" type="button" onClick={() => setReload(value => value + 1)} disabled={loading} aria-label="Refresh allocations" title="Refresh allocations">
          <RefreshCw size={18} />
        </button>
      </div>

      <p className="email-allocations__notice">
        Allocation records map a tenant and its enabled Exchange connection to a mailbox object ID. They do not provision mailboxes or grant Exchange Mail.Send permission.
      </p>

      {error && <div className="email-allocations__message email-allocations__message--error" role="alert">{error}</div>}
      {notice && <div className="email-allocations__message email-allocations__message--success" role="status">{notice}</div>}

      <section className="email-allocations__connection-setup" aria-labelledby="exchange-connection-title">
        <div className="email-allocations__connection-heading">
          <div>
            <h3 id="exchange-connection-title">Register Exchange connection</h3>
            <p>Uses this environment's configured Exchange application credential from Key Vault. Registration validates the credential pair but does not grant Exchange permissions or provision mailboxes.</p>
          </div>
        </div>
        {registrationError && <div className="email-allocations__message email-allocations__message--error" role="alert">{registrationError}</div>}
        {registrationLoading ? <div className="loading">Loading Exchange connection options…</div> : registrationOptions && (
          <>
            <div className="email-allocations__credential-summary">
              <span>Application client ID</span><code>{registrationOptions.clientId}</code>
              <span>Sender Key Vault reference</span><code>{registrationOptions.credentialReference}</code>
            </div>
            {registrationOptions.tenants.length === 0 ? (
              <p className="email-allocations__empty-connections">No active tenants with an Entra organization are available for a new connection.</p>
            ) : (
              <form className="email-allocations__connection-form" onSubmit={handleRegisterConnection}>
                <label>
                  Tenant and Exchange organization
                  <select value={registrationTenantId} onChange={event => setRegistrationTenantId(event.target.value)} required disabled={registeringConnection}>
                    {registrationOptions.tenants.map(tenant => (
                      <option key={tenant.tenantId} value={tenant.tenantId}>
                        {tenant.tenantName} · Exchange org {tenant.exchangeTenantId}
                      </option>
                    ))}
                  </select>
                </label>
                <button className="email-allocations__create" type="submit" disabled={registeringConnection || !registrationTenantId}>
                  <Plus size={17} />{registeringConnection ? 'Registering…' : 'Register connection'}
                </button>
              </form>
            )}
          </>
        )}
      </section>

      <form className="email-allocations__form" onSubmit={handleCreate}>
        <label>
          Enabled tenant connection
          <select value={connectionId} onChange={event => setConnectionId(event.target.value)} required disabled={loading || connections.length === 0}>
            <option value="">Select a tenant connection</option>
            {connections.map(connection => (
              <option key={`${connection.tenantId}:${connection.connectionId}`} value={`${connection.tenantId}:${connection.connectionId}`}>
                {connection.tenantName} · Exchange org {connection.exchangeTenantId} · {connection.connectionId}
              </option>
            ))}
          </select>
        </label>
        <label>
          Mailbox object ID
          <input
            type="text"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            value={mailboxObjectId}
            onChange={event => setMailboxObjectId(event.target.value)}
            placeholder="Directory object UUID"
            pattern="[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}"
            title="Enter a mailbox object UUID"
            required
            disabled={loading || saving}
          />
        </label>
        <button className="email-allocations__create" type="submit" disabled={loading || saving || !connectionId || !mailboxObjectId.trim()}>
          <Plus size={17} />
          {saving ? 'Saving…' : 'Allocate mailbox'}
        </button>
        {connections.length === 0 && !loading && <span className="email-allocations__empty-connections">No enabled tenant connections are available.</span>}
      </form>

      <div className="email-allocations__table-wrap">
        <table className="email-allocations__table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Exchange organization</th>
              <th>Mailbox object ID</th>
              <th>Connection</th>
              <th>Registration</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="email-allocations__empty">Loading allocations…</td></tr>
            ) : allocations.length === 0 ? (
              <tr><td colSpan={6} className="email-allocations__empty">No mailbox allocations found.</td></tr>
            ) : allocations.map(allocation => {
              const key = allocationKey(allocation);
              return (
                <tr key={key}>
                  <td className="email-allocations__tenant">{allocation.tenantName}<small>{allocation.tenantId}</small></td>
                  <td className="email-allocations__mono">{allocation.exchangeTenantId}</td>
                  <td className="email-allocations__mono">{allocation.mailboxObjectId}</td>
                  <td>
                    <span className={`email-allocations__connection ${allocation.connectionEnabled ? 'is-enabled' : 'is-disabled'}`}>
                      {allocation.connectionEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <small className="email-allocations__sub-id">{allocation.connectionId}</small>
                  </td>
                  <td>{allocation.isRegistered ? <span className="email-allocations__registered">Registered{allocation.registeredMailboxName ? ` · ${allocation.registeredMailboxName}` : ''}</span> : 'Unregistered'}</td>
                  <td className="email-allocations__actions">
                    {allocation.isRegistered ? <span className="email-allocations__locked" title="A mailbox registration uses this allocation">In use</span> : confirming === key ? (
                      <span className="email-allocations__confirm">
                        <button type="button" className="email-allocations__confirm-remove" onClick={() => void handleRemove(allocation)} disabled={saving} aria-label="Confirm revoke allocation"><Check size={16} />Confirm</button>
                        <button type="button" className="email-allocations__icon-button" onClick={() => setConfirming(null)} aria-label="Cancel revoke"><X size={16} /></button>
                      </span>
                    ) : (
                      <button type="button" className="email-allocations__revoke" onClick={() => setConfirming(key)} disabled={saving} title="Revoke unused allocation">
                        <Trash2 size={16} /><span>Revoke</span>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="email-allocations__pagination">
        <span>{allocations.length ? `Showing ${skip + 1}–${skip + allocations.length}` : 'No rows'}{hasNext ? ' · more available' : ''}</span>
        <div>
          <button type="button" className="email-allocations__icon-button" onClick={() => setSkip(value => Math.max(0, value - PAGE_SIZE))} disabled={skip === 0 || loading} aria-label="Previous allocation page" title="Previous page"><ChevronLeft size={18} /></button>
          <button type="button" className="email-allocations__icon-button" onClick={() => setSkip(value => value + PAGE_SIZE)} disabled={!hasNext || loading} aria-label="Next allocation page" title="Next page"><ChevronRight size={18} /></button>
        </div>
      </div>
    </section>
  );
}